import { useSelector } from 'react-redux';
import { Box, Flex, HStack, Progress, Spacer, Stack } from '@chakra-ui/react';
import {
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
} from '@/components/v4';
import { FOOTER_BUTTON_WIDTH, fixedModalHeight } from '../constants';
import { getRemovingTrunksLabel } from '../utils/bulkUpdate';

const plural = (count, word) => `${count} ${count === 1 ? word : `${word}s`}`;

const getTitle = (run, isRunning, failedCount) => {
    if (run.isCancelling) return 'Cancelling...';
    if (isRunning) return 'Processing numbers...';
    if (run.notSent > 0) return 'Update cancelled';
    return failedCount > 0 ? 'Update finished with errors' : 'Update complete';
};

const getNote = (run, isRunning, failedCount) => {
    if (run.isCancelling) {
        return "Finishing the updates already sent. The rest won't be sent.";
    }
    if (isRunning) {
        return 'This may take a few minutes. You can hide this and keep working on this page, but refreshing or leaving the page will cancel the remaining updates.';
    }
    if (run.notSent > 0) {
        return `${plural(run.notSent, 'number')} weren't sent and have been left unchanged.`;
    }
    if (failedCount === 0) return 'All selected numbers have been processed.';
    return `${plural(failedCount, 'number')} could not be updated. They have been left unchanged.`;
};

/**
 * Progress and results of a Bulk Update run, modelled on the Add Number
 * Blocks SubmitProgressModal. While the run is going it can be hidden (the
 * page header keeps showing progress); once done, closing it clears the run.
 */
const BulkUpdateProgressModal = ({
    run,
    isOpen,
    onHide,
    onCancel,
    onClose,
    onSelectFailed,
}) => {
    const { darkMode, colorScheme } = useSelector((state) => state.settings);
    const isRunning = run.status === 'running';
    const failedCount = run.failed.length;
    const percent = run.total ? (run.processed / run.total) * 100 : 0;
    const isRemovingTrunks = isRunning && run.removingTrunks > 0;
    const borderColor = darkMode ? 'dark.tcap-borderGray' : 'gray.200';

    const counters = [
        { label: 'Successful', value: run.success, color: 'green.500' },
        { label: 'Failed', value: failedCount, color: 'red.500' },
        { label: 'No Change (Ignored)', value: run.ignored, color: 'gray.500' },
        ...(run.notSent > 0
            ? [{ label: 'Not Sent', value: run.notSent, color: 'orange.500' }]
            : []),
    ];

    return (
        <Modal
            isOpen={isOpen && run.status !== 'idle'}
            onClose={isRunning ? onHide : onClose}
            closeOnOverlayClick
            size="2xl">
            <ModalOverlay />
            <ModalContent
                bg={darkMode ? 'gray.800' : 'white'}
                sx={fixedModalHeight('560px')}>
                <ModalHeader color={darkMode ? 'white' : 'gray.900'}>
                    {getTitle(run, isRunning, failedCount)}
                </ModalHeader>
                <ModalCloseButton />
                {/* The failed list fills the space left and scrolls */}
                <ModalBody display="flex" flexDirection="column" minH={0}>
                    <Stack spacing={4} flex="1" minH={0}>
                        <Text color={darkMode ? 'gray.100' : 'gray.700'}>
                            {getNote(run, isRunning, failedCount)}
                        </Text>
                        <Box>
                            <Progress
                                value={percent}
                                colorScheme={colorScheme}
                                size="sm"
                                borderRadius="md"
                                hasStripe={isRunning}
                                isAnimated={isRunning}
                                isIndeterminate={isRemovingTrunks}
                                aria-label="Update progress"
                            />
                            <Text fontSize="sm" textAlign="right" mt={1}>
                                {isRemovingTrunks
                                    ? getRemovingTrunksLabel(run.removingTrunks)
                                    : `${run.processed.toLocaleString()} of ${run.total.toLocaleString()}`}
                            </Text>
                        </Box>
                        {/* The failed list (or a spacer) fills the space, so the
                            counts sit at the bottom */}
                        {failedCount > 0 ? (
                            <Flex
                                direction="column"
                                flex="1"
                                minH={0}
                                border="1px solid"
                                borderColor={darkMode ? 'red.300' : 'red.200'}
                                borderRadius="md"
                                overflow="hidden">
                                <Text
                                    px={3}
                                    py={2}
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    bg={darkMode ? 'whiteAlpha.100' : 'red.50'}
                                    color={darkMode ? 'red.300' : 'red.700'}>
                                    Failed numbers
                                </Text>
                                <Stack
                                    spacing={0}
                                    flex="1"
                                    minH={0}
                                    overflowY="auto">
                                    {run.failed.map((failure) => (
                                        <Flex
                                            key={failure.id}
                                            gap={4}
                                            px={3}
                                            py={1.5}
                                            fontSize="sm"
                                            borderTop="1px solid"
                                            borderColor={borderColor}
                                            align="flex-start">
                                            <Text
                                                fontSize="sm"
                                                w="140px"
                                                flexShrink={0}
                                                m={0}>
                                                {failure.e164}
                                            </Text>
                                            <Text
                                                fontSize="sm"
                                                color="gray.500"
                                                flex="1"
                                                m={0}>
                                                {failure.reason}
                                            </Text>
                                        </Flex>
                                    ))}
                                </Stack>
                            </Flex>
                        ) : (
                            <Spacer />
                        )}
                        <HStack
                            spacing={6}
                            flexWrap="wrap"
                            px={4}
                            py={3}
                            borderRadius="md"
                            border="1px solid"
                            borderColor={borderColor}
                            bg={darkMode ? 'dark.tcap' : 'gray.50'}>
                            {counters.map((counter) => (
                                <Text
                                    key={counter.label}
                                    fontWeight="semibold"
                                    color={counter.color}>
                                    {counter.label} :{' '}
                                    {counter.value.toLocaleString()}
                                </Text>
                            ))}
                        </HStack>
                    </Stack>
                </ModalBody>
                {/* Always shown so the layout doesn't jump when the run ends */}
                <ModalFooter justifyContent="flex-start" gap={3}>
                    <Button
                        minW={FOOTER_BUTTON_WIDTH}
                        onClick={isRunning ? onHide : onClose}>
                        {isRunning ? 'Hide' : 'Done'}
                    </Button>
                    {isRunning && (
                        <Button
                            variant="outline"
                            colorScheme="red"
                            minW={FOOTER_BUTTON_WIDTH}
                            isLoading={run.isCancelling}
                            loadingText="Cancelling"
                            onClick={onCancel}>
                            Cancel Update
                        </Button>
                    )}
                    {!isRunning && failedCount > 0 && (
                        <Button variant="outline" onClick={onSelectFailed}>
                            Select failed numbers
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default BulkUpdateProgressModal;
